import { useLazyQuery, useMutation } from "@apollo/client";
import { DataQuery, Mutation } from "@models/ISchema";
import { REFERENCES_QUERY } from "./graphql/references.query";
import { REFERENCES_CATEGORIES_QUERY } from "./graphql/referencesCategories.query";
import {
  CreateOrDeleteReferencesPayload,
  CreateOrDeleteReferencesProps,
  GetExternalReferencesPayload,
  ReferencesCategoriesProps,
} from "@services/types/references/referencesTypes";
import { REFERENCES_CRUD_MUTATION } from "./graphql/referencesCrud.mutation";
import { EXTERNAL_REFERENCES_QUERY } from "./graphql/externalReferences.query";

export const useGetReferences = () => {
  const [getReferences, { loading, data }] = useLazyQuery<{ data: DataQuery }>(REFERENCES_QUERY);

  const handleGetReferences = ({
    scenarioId,
    originId,
  }: {
    scenarioId: string;
    originId: string;
  }) => {
    getReferences({
      variables: { scenarioId, originId },
    });
  };

  return {
    loading,
    pageInfo: data?.data?.references?.pageInfo ?? {},
    references: data?.data?.references?.items ?? [],
    getReferences: handleGetReferences,
  };
};

export const useGetReferencesCategories = (props?: ReferencesCategoriesProps) => {
  const { onCompleted } = props ?? {};

  const [getReferencesCategories, { loading, data }] = useLazyQuery<{ data: DataQuery }>(
    REFERENCES_CATEGORIES_QUERY,
    {
      onCompleted: data => {
        onCompleted?.(data?.data?.referenceCategories);
      },
    },
  );

  const handleGetReferencesCategories = ({
    scenarioId,
    originId,
  }: {
    scenarioId: string;
    originId: string;
  }) => {
    getReferencesCategories({
      variables: { scenarioId, originId },
    });
  };

  return {
    loading,
    pageInfo: data?.data?.referenceCategories?.pageInfo ?? {},
    referenceCategories: data?.data?.referenceCategories?.items ?? [],
    getReferencesCategories: handleGetReferencesCategories,
  };
};

export const useCreateOrDeleteReferences = (props?: CreateOrDeleteReferencesProps) => {
  const { onCompleted } = props ?? {};

  const [createOrDeleteReferences, { loading, data }] = useMutation<Mutation>(
    REFERENCES_CRUD_MUTATION,
    {
      onCompleted: data => {
        onCompleted?.(data?.cube?.editGroupTexts);
      },
    },
  );

  const handleCreateOrDeleteReferences = ({
    scenarioId,
    originId,
    dryRun = false,
    groupId,
    references = [],
  }: CreateOrDeleteReferencesPayload) => {
    createOrDeleteReferences({
      variables: {
        scenarioId,
        originId,
        input: { dryRun, groupId, references },
      },
    });
  };

  return {
    loading,
    data,
    handleCreateOrDeleteReferences,
  };
};

export const useGetExternalReferences = () => {
  const [getExternalReferences, { loading, data }] = useLazyQuery<{ data: DataQuery }>(
    EXTERNAL_REFERENCES_QUERY,
  );

  const handleGetExternalReferences = ({
    scenarioId,
    originId,
    crn,
    term,
  }: GetExternalReferencesPayload) => {
    return getExternalReferences({
      variables: { scenarioId, originId, crn, term },
    });
  };

  return {
    loading,
    data: data?.data?.externalReferences ? [data?.data?.externalReferences] : [],
    handleGetExternalReferences,
  };
};
